import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useTheme } from '../context/ThemeContext'
import { DOWNLOAD_URL } from '../pages/DownloadRedirect'

interface AppDownloadQRCodeProps {
  /**
   * The URL to encode in the QR code (defaults to the portal's /download page,
   * which redirects the scanning device to the right app store)
   */
  downloadUrl?: string
  /**
   * Size of the QR code in pixels (default: 200)
   */
  size?: number
  /**
   * Show a title above the QR code
   */
  showTitle?: boolean
  /**
   * Custom title text (defaults to "Scan to Download b4igo App")
   */
  title?: string
  /**
   * Show description text below the QR code
   */
  showDescription?: boolean
  /**
   * Custom description text
   */
  description?: string
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Level of error correction (L, M, Q, H)
   * L - Low (7% of codewords can be restored)
   * M - Medium (15%) - default
   * Q - Quartile (25%)
   * H - High (30%)
   */
  level?: 'L' | 'M' | 'Q' | 'H'
  /**
   * Include b4igo logo in center of QR code
   */
  includeLogo?: boolean
}
const FallbackURL = process.env.REACT_APP_B4IGO_LANDING_PAGE
const AppDownloadQRCode: React.FC<AppDownloadQRCodeProps> = ({
  downloadUrl = DOWNLOAD_URL,
  size = 200,
  showTitle = true,
  title = 'Scan to Download b4igo App',
  showDescription = true,
  description = 'Scan this QR code with your phone camera to download the b4igo app',
  className = '',
  level = 'M',
  includeLogo = false,
}) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  // QR code should always be black on white for best scanability
  // Only the outer container changes based on theme
  const bgColor = isDark ? '#1e1e2d' : '#ffffff'

  return (
    <div
      className={`d-flex flex-column align-items-center justify-content-center p-4 ${className}`}
      style={{
        backgroundColor: bgColor,
        borderRadius: '12px',
        boxShadow: isDark
          ? '0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
    >
      {showTitle && (
        <h4
          className='mb-3 text-center'
          style={{
            color: isDark ? '#ffffff' : '#181C32',
            fontWeight: 600,
          }}
        >
          {title}
        </h4>
      )}

      <div
        className='position-relative'
        style={{
          padding: '16px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <QRCodeSVG
          value={downloadUrl}
          size={size}
          level={level}
          fgColor='#000000'
          bgColor='#ffffff'
          includeMargin={true}
          imageSettings={
            includeLogo
              ? {
                src: '/media/logos/orange-robot-bold.svg',
                x: undefined,
                y: undefined,
                height: size * 0.22,
                width: size * 0.2,
                excavate: true,
              }
              : undefined
          }
        />
      </div>

      {showDescription && (
        <p
          className='mt-3 mb-0 text-center'
          style={{
            color: isDark ? '#92929f' : '#7E8299',
            fontSize: 'var(--fs-body-sm)',
            maxWidth: '300px',
          }}
        >
          {description}
        </p>
      )}

      <div className='mt-3'>
          <span className='text-muted'>or go to </span><br/>
        <a href={FallbackURL} target='_blank' rel='noopener noreferrer' className='text-primary'
          style={{
            color: isDark ? '#92929f' : '#A1A5B7',
            fontSize: 'var(--fs-small)',
          }}
        >
          {FallbackURL}
        </a>
      </div>
    </div>
  )
}

export default AppDownloadQRCode
